/**
 * Aura Incorporation preset (my_preset / sg / aura) — 자체완결 모듈.
 *
 *   my_preset/
 *     sg/
 *       aura/                     ← 이 모듈 (Singapore, Aura Partners 문서셋)
 *         incorporation-pre/      문서 컴포넌트(공용 8종 + Aura 고유 6종) + cdd 플러밍
 *         components/PreSetAuraIncorporationPanel.vue
 *         mappers/mapAuraIncorporationToPreValues.ts   ← Aura 전용 매퍼
 *         chatflows/auraIncorporationFlow.ts
 *         utils/normalizeSgPreFormDataForWord.ts
 *
 * 배선: main.ts 에서 `import '@/features/startTask/my_preset/sg/aura'` 하면
 *   이 모듈 로드 시 registerAuraIncorporationPreset() 가 자동 실행되어
 *   chatFlowRegistry + panelRegistry 에 등록된다.
 *   매칭 = My Preset(클라이언트 커스텀)에서 온 'incorporation' 선택 (priority 50 > 기존 SG 40).
 *   → 일반 공통 프리셋 incorporation 은 그대로 기존 SG 패널이 처리.
 */
import { registerPanel } from '@/features/startTask/preset_task/shared/preset/panels/panelRegistry';
import { registerChatFlow } from '@/features/startTask/preset_task/shared/preset/chatflows/chatFlowRegistry';
import type { PreSetSelection } from '@/features/startTask/preset_task/shared/preset/types/preset.types';
import PreSetAuraIncorporationPanel from './components/PreSetAuraIncorporationPanel.vue';
import { auraIncorporationFlow } from './chatflows/auraIncorporationFlow';

export { default as PreSetAuraIncorporationPanel } from './components/PreSetAuraIncorporationPanel.vue';
export { default as AuraIncorporationPackageDoc } from './incorporation-pre/AuraIncorporationPackageDoc.vue';
export { auraIncorporationFlow } from './chatflows/auraIncorporationFlow';
export {
  mapAuraIncorporationToPreValues,
  type AuraIncPreIncludes,
  type AuraIncPreMapped,
} from './mappers/mapAuraIncorporationToPreValues';

/** 라우팅 키 = 공통코드(클라이언트 접두어 CAU = Aura). 코드 전용 매칭. */
export const AURA_INCORPORATION_TASK_CODES: string[] = ['CAU001'];

/**
 * 선택이 Aura 인코퍼레이션인지 판정.
 *  - My Preset(클라이언트 커스텀)에서 온 'incorporation' 선택이면 Aura 전용 패널 사용.
 *  - 또는 지정된 task code 와 일치할 때.
 * 일반(공통) 프리셋 incorporation 은 isMyPreset 이 아니므로 기존 SG 패널이 처리.
 */
export function matchesAuraIncorporation(sel: PreSetSelection): boolean {
  // 코드 전용 매칭 — 접두어 코드(CAU=Aura)가 유일 라우팅 키. 이름 fallback 제거(고객사 누수 방지).
  const code = (sel.task.TASK_CODE || '').toUpperCase();
  return AURA_INCORPORATION_TASK_CODES.map((c) => c.toUpperCase()).includes(code);
}

/** 이 프리셋을 chatflow + panel 레지스트리에 등록 (import 시 자동 실행). */
export function registerAuraIncorporationPreset(): void {
  registerChatFlow(auraIncorporationFlow);
  registerPanel({
    id: 'aura-incorporation',
    // SG Incorporation(Pre) 패널(priority 40)보다 먼저 잡히도록 더 높게
    priority: 50,
    match: (sel) => matchesAuraIncorporation(sel),
    component: PreSetAuraIncorporationPanel,
    meta: { docType: 'SG_INCORPORATION_PRE', studio: true },
  });
}

// 모듈 import 시 자동 등록 (main.ts 의 `import '@/features/startTask/my_preset/sg/aura'`)
registerAuraIncorporationPreset();

// Aura AGM (CAU002, Sole Member) — 자체완결 하위 모듈. import 시 registerAuraAgmPreset() 자동 실행.
import './agm';
// Aura AGM (Multiple Shareholders) — 자체완결 하위 모듈. import 시 registerAuraAgmMultiPreset() 자동 실행.
import './agm-multi';
// Aura AGM (Audited · Sole Member — Corporate, CAU004) — 자체완결 하위 모듈. 자동 등록.
import './agm-sole-corp';
// Aura AGM (Audited · Multiple member, CAU005) — 자체완결 하위 모듈. 자동 등록.
import './agm-multi-audited';
// Aura On Board (New Take Over Client, CAU006) — 자체완결 하위 모듈. 자동 등록.
import './onboard';
// Aura AGM (Audited · Sole Member — Individual, CAU007) — 자체완결 하위 모듈. 자동 등록.
import './agm-sole-indiv';
// Aura Share Certificate (ordinary shares, CAU008) — 자체완결 하위 모듈. 자동 등록.
import './share-cert';
